import * as Highcharts from 'highcharts/highstock';

import { apply } from './theme/dark-unica';
apply(Highcharts);

import { getNavigatorOHLCData, getOHLCData, getStudyData } from './service/httpService';
import TimeLevel, { TimeLevelEnum } from './enum/timelevel';
import { addTimelevelToDate, convertStringToDate } from './utils/date';
import { ChartProperties, ChartState, ExtremeUpdateEvent, OldChartProperties } from './interface/chart';
import { OHLCData } from './interface/httpResponse';
import { chartConfig, purpleColor, redColor, whiteColor } from './constant/constants';
import { StudyType, StudyTypeEnum } from './enum/studyType';
import { drawCurrencyOnChart, drawLegendLabelOnChart, drawTimePeriodOnChart, getDateFormatterByTimelevel, getRangeSelectorOptions } from './utils/chart';
import { PointType } from './enum/pointType';
import { getSlopeOfLine } from './utils/math';
import { AxisType, AxisTypeEnum } from './enum/axisType';

// https://api.highcharts.com/class-reference/Highcharts.StockChart
export default class Chart {

    private properties: ChartProperties;
    private state: ChartState;
    private instance: any;

    constructor(props: OldChartProperties) {
        this.setup(props);
    }

    private clearState() {
        this.state = {
            xMaxExtreme: 0,
            xMinExtreme: 0,
            yMaxExtreme: 0,
            yMinExtreme: 0,
            currencyName: '',
            marketName: '',
            ohlcData: [],
            technicalLines: [],
            electedReversals: {}
        };
    }

    private setup(props: OldChartProperties) {
        this.properties = {
            containerId: props.ChartDiv,
            currency: '',
            selectedEndDate: props.SelectedEndDate,
            endDate: props.EndDate,
            marketId: props.MarketId,
            selectedStartDate: props.SelectedStartDate,
            startDate: props.StartDate,
            timelevel: TimeLevel.GetByValue(props.TimeLevel?.value),
            studyType: props.CurrentStudy ? (Array.isArray(props.CurrentStudy) ? props.CurrentStudy.map(x => StudyType.GetByValue(x.value)) : [StudyType.GetByValue(props.CurrentStudy.value)]) : undefined,
            selectedTimeUnits: props.SelectedTimeUnits,
            hideNavigator: props.HideNavigator,
            iteration: props.Iteration,
            hideTimelevel: props.HideTimelevel,
            showElectedReversals: props.ShowElectedReversals
        };
        this.clearState();
    }

    private moveCrosshair(direction: number) {
        const priceDataSerie = this.instance.get(chartConfig.priceDataId);
        const points = priceDataSerie.points.filter((p: any) => p.close != null);

        if(this.state.selectedPointIdx == null) {
            this.state.selectedPointIdx = points.length! - 1;
        } else {
            this.state.selectedPointIdx += direction;
        }

        if(this.state.selectedPointIdx < 0)
            this.state.selectedPointIdx = points.length - 1;
        else if(this.state.selectedPointIdx >= points.length)
            this.state.selectedPointIdx = 0;

        points[this.state.selectedPointIdx].onMouseOver();
    }

    private registerArrowKeyAction() {
        window.addEventListener('keydown', ((e: any) => {
            if(e.keyCode === 37) {
                this.moveCrosshair(-1);
            } else if(e.keyCode === 39) {
                this.moveCrosshair(1);
            }
        }).bind(this));
    }

    private onExtremeUpdate(e: ExtremeUpdateEvent) {
        if(!e.trigger)
            return;

        const { min, max } = { min: Math.round(e.min), max: Math.round(e.max) };

        this.state.xMaxExtreme = max;
        this.state.xMinExtreme = min;

        setTimeout(() => {
            if(min === this.state.xMinExtreme && max === this.state.xMaxExtreme) {
                this.reloadChart();
            }
        }, 150);
    }

    private extendData(data: OHLCData[]) {
        const size = this.properties.timelevel === TimeLevelEnum.Daily ? 44 : 33;

        for(let i = 0; i < size; i++) {
            const cData = data[data.length - 1];
            const date = new Date(cData.x);
            data.push({
                x: addTimelevelToDate(date, this.properties.timelevel!, 1).getTime()
            });
        }
        return data;
    }

    private async addStudy(redraw: boolean) {
        if(this.properties.studyType && this.properties.studyType.length) {
            // calculate heights
            const studyHeight = 15;
            const ohlcChartHeight = 87 - (studyHeight * this.properties.studyType.filter(x => !StudyType.isOHLCStudy(x)).length);
            // draw them
            await Promise.all(this.properties.studyType.map(async (study: StudyTypeEnum, idx: number): Promise<void> => {
                const { studyValue, isOHLC } = { studyValue: StudyType.GetByValue(Number(study)), isOHLC: StudyType.isOHLCStudy(study) };
                const response = await getStudyData(
                    this.properties.marketId,
                    this.properties.timelevel!,
                    studyValue,
                    this.state.xMaxExtreme,
                    this.state.xMinExtreme,
                    true
                );

                if(!response || !response.studies || !response.studies.length) {
                    return;
                }

                const studyAxisName = isOHLC ? chartConfig.yAxisName : (chartConfig.studyAxisName + idx);

                if(!isOHLC) {
                    const studyAxisInstance = this.instance.get(studyAxisName);
                    if(studyAxisInstance && !isOHLC)
                        studyAxisInstance.remove();

                    this.instance.yAxis[0].options.height = `${ohlcChartHeight}%`;

                    this.instance.addAxis({
                        id: studyAxisName,
                        labels: {
                            enabled: true
                        },
                        title: {
                            text: ''
                        },
                        top: `${85 - (studyHeight * idx)}%`,
                        height: `${studyHeight}%`,
                        offset: 0,
                        width: 3,
                        //floor: 0
                    }, false, redraw, false);
                }
                const wrapper = StudyType.getStudyWrapper(studyValue, studyAxisName);
                if(wrapper && !this.instance.get(wrapper.id)) {
                    this.instance.addSeries(wrapper, redraw, false);
                }

                response.studies.forEach((study) => {
                    var seriesDataObj = {
                        id: study.name,
                        name: study.name,
                        data: study.data,
                        yAxis: studyAxisName,
                        type: study.type,
                        borderWidth: 0,
                        color: study.color,
                        borderColor: study.color,
                        enableMouseTracking: true,
                        showInLegend: false,
                        linkedTo: wrapper?.id,
                        marker: {
                            enabled: false
                        },
                        tooltip: {
                            valueDecimals: 2
                        }
                    } as any;

                    if (studyValue === StudyTypeEnum.Energy) {
                        seriesDataObj.negativeColor = "red";
                    }

                    //remove if it exists
                    let existingStudy = this.instance.get(seriesDataObj.id);
                    if(existingStudy) {
                        existingStudy.setData(study.data, redraw, false);
                    } else {
                        this.instance.addSeries(seriesDataObj, redraw, false);
                    }
                });
            }));
        }
    }

    private async reloadChart(firstLoad: boolean = false) {
        try {
            this.instance.showLoading('Loading');
            const response = await getOHLCData(
                this.properties.marketId,
                this.properties.timelevel!,
                this.state.xMaxExtreme,
                /* If first load, add a margin of 90 days to minExtreme to avoid issues with technical lines where sometimes
                   the technical line's date is lower than xMinExtreme and the line is not displayed */
                this.state.xMinExtreme - (firstLoad ? (1000*3600*24*90) : 0),
                true,
                this.properties.currency,
                -1
            );

            let extendedData: OHLCData[] = [];
            if(response && response.ohlcData && response.ohlcData.length) {
                extendedData = this.extendData(response.ohlcData);

                let [ minY, maxY ] = [ extendedData[0].low || Number.MAX_VALUE, extendedData[0].high || Number.MIN_VALUE ];
                extendedData.forEach((data) => {
                    minY = data.low ? Math.min(data.low, minY) : minY;
                    maxY = data.high ? Math.max(data.high, maxY) : maxY;
                    data.custom = {
                        electedReversals: this.state.electedReversals[data.x]
                    };
                });

                this.state = {
                    ...this.state,
                    yMaxExtreme: maxY,
                    yMinExtreme: minY,
                    ohlcData: extendedData
                };

                const yAxis = this.instance.get(chartConfig.yAxisName);
                yAxis.setExtremes(this.state.yMinExtreme, this.state.yMaxExtreme, false);
            }

            this.instance.series[0].setData(extendedData, false);

            await this.addStudy(false);

            this.instance.hideLoading();
            this.instance.redraw();
        } catch(err) {
            console.error(err);
        }
    }

    private enablePlotLineTooltip(value: string) {
        if ($("#chartTooltipPlot").length === 0) {
            var $tooltip = $("<div>", {
                'id': "chartTooltipPlot", 'class': "chart-tooltip"
            });
            $tooltip.html("");
            $("body").append($tooltip);
        }

        return {
            mouseover: function (e: any) {
                let posY = value.indexOf('\n') > -1 ? 60 : 40;
                $("#chartTooltipPlot").html((value as any).replaceAll('\n', '<br>'));
                $("#chartTooltipPlot").show();
                $("#chartTooltipPlot").css("top", +e.pageY - posY + "px").css("left", +e.pageX - 25 + "px").css('text-align', 'center');
                var elem: any = document.elementFromPoint(e.pageX, e.pageY);
                elem.setAttribute("stroke-width", "3");

            },
            mouseout: function (e: any) {
                $(e.target)[0].attributes['stroke-width'].value = 1;
                $("#chartTooltipPlot").hide();
            },
        }
    }

    AddPlotLine(data: any, axisId: number | string, axisType: any, enableTooltip: boolean, tooltip?: string) {
        if (axisId === null) {
            axisId = 0;
        }
        if (enableTooltip) {
            data.events = this.enablePlotLineTooltip(tooltip ?? data.value);
        }

        const axis = AxisType.getByValue(axisType.value);

        if (axis === AxisTypeEnum.Y) {
            this.instance.yAxis[parseInt(axisId as string)].addPlotLine(data);
        } else {
            this.instance.xAxis[parseInt(axisId as string)].addPlotLine(data);
        }

    }

    private drawLineOnChart(startingIndex: number, endingIndex: number | null, startingValue: number, slope: number, seriesName: string, addToTechTable: boolean, techIndex: number, color: string) {
        var tempData = [];
        var unitsAfter = 0;

        if (endingIndex === null) {
            endingIndex = this.state.ohlcData.length - 1;
        }

        for (var i = startingIndex; i <= endingIndex; i++) {


            var newYValue = (startingValue + (unitsAfter * (slope)));
            var newXValue = this.state.ohlcData[i]?.x;

            var obj = [newXValue, newYValue];
            tempData.push(obj);

            if (addToTechTable) {
                //addToTechnicalTable(newXValue, newYValue, seriesName, techIndex);
            }

            unitsAfter++;
        }

        if (chartConfig.autoChangeLineColor && color === null) {
            if (slope > 0) {
                color = whiteColor;
            } else if (slope === 0) {
                color = purpleColor;
            } else {
                color = redColor;
            }
        } else {
            if (color === null) {
                color = purpleColor;
            }
        }

        //currentState.TechnicalLines.push({ startingIndex: startingIndex, endingIndex: endingIndex, startingValue: startingValue, slope: slope, seriesName: seriesName, addToTechTable: addToTechTable, techIndex: techIndex, color: color });
        const wrapper = StudyType.getStudyWrapper(StudyTypeEnum.TechnicalLines, chartConfig.yAxisName);
        if(wrapper && !this.instance.get(wrapper.id)) {
            this.instance.addSeries(wrapper, false, false);
        }

        var seriesDataObj = {
            data: tempData,
            name: seriesName,
            width: 2,
            color: color,
            showInLegend: false,
            linkedTo: wrapper?.id,
            marker: {
                enabled: false
            },
            allowPointSelect: false,
            tooltip: {
                valueDecimals: 2
            }
        } as any;

        seriesDataObj.point = {};
        //seriesDataObj.point.events = enableSeriesTooltip();

        var series = this.instance.addSeries(seriesDataObj);

        return series.index;
    }

    AddTechnicalChannelCustom(firstPointDate: string, firstPointType: any, secondPointDate: string, secondPointType: any, thirdPointDate: string, thirdPointType: any, name: string, color: string) {
        this.AddTechnicalChannel(firstPointDate, firstPointType, secondPointDate, secondPointType, thirdPointDate, thirdPointType, name, color);
    }

    AddTechnicalChannel(firstPointDate: string, firstPointType: any, secondPointDate: string, secondPointType: any, thirdPointDate: string, thirdPointType: any, name: string, color: string) {
        var firstPointDateIndex = 0;
        var secondPointDateIndex = 0;
        var thirdPointDateIndex = 0;

        var firstPointDateUtc = new Date(convertStringToDate(firstPointDate)).toISOString();
        var secondPointDateUtc = new Date(convertStringToDate(secondPointDate)).toISOString();
        var thirdPointDateUtc = new Date(convertStringToDate(thirdPointDate)).toISOString();

        var firstPointDateY = 0;
        var secondPointDateY = 0;
        var thirdPointDateY = 0;

        const firstPt = PointType.getByValue(firstPointType.value);
        const secondPt = PointType.getByValue(secondPointType.value);
        const thirdPt = PointType.getByValue(thirdPointType.value);

        var series = this.state.ohlcData;

        for (var i = 0; i < series.length - 1; i++) {
            var curPoint = series[i];
            var curDate = new Date(curPoint.x).toISOString();


            if (curDate === firstPointDateUtc) {
                firstPointDateIndex = i;
                firstPointDateY = PointType.getValueByPointType(curPoint, firstPt);
            }
            if (curDate === secondPointDateUtc) {
                secondPointDateIndex = i;
                secondPointDateY = PointType.getValueByPointType(curPoint, secondPt);
            }
            if (curDate === thirdPointDateUtc) {
                thirdPointDateIndex = i;
                thirdPointDateY = PointType.getValueByPointType(curPoint, thirdPt);
            }
        }

        if (firstPointDateY !== 0 && secondPointDateY !== 0 && thirdPointDateY !== 0) {
            var slope = getSlopeOfLine(firstPointDateIndex, firstPointDateY, secondPointDateIndex, secondPointDateY);
            var techIndex = this.state.technicalLines.length + 1;

            this.drawLineOnChart(firstPointDateIndex, null, firstPointDateY, slope, name, true, techIndex, color);
            this.drawLineOnChart(thirdPointDateIndex, null, thirdPointDateY, slope, name, true, techIndex, color);

            this.state.technicalLines.push({
                startingDate: firstPointDate,
                endingDate: null,
                startingValue:
                firstPointDateY,
                slope: slope,
                seriesName: name,
                addToTechTable: true,
                techIndex: techIndex,
                color: color
            });

            this.state.technicalLines.push({
                startingDate: thirdPointDate,
                endingDate: null,
                startingValue: thirdPointDateY,
                slope: slope,
                seriesName: name,
                addToTechTable: true,
                techIndex: techIndex,
                color: color
            });
        }

    }

    async update(props: OldChartProperties) {
        const oldProperties = this.properties;
        this.setup(props);

        const keys = Object.keys(this.properties).concat(Object.keys(oldProperties));
        keys.forEach(key => {
            this.properties[key] = this.properties[key] ? this.properties[key] : oldProperties[key];
        });

        this.clearState();
        await this.load();
    }

    async Update(props: OldChartProperties, callback: any) {
        await this.update(props);

        if(callback && typeof callback === 'function') {
            callback();
        }
    }

    async Load(callback: any) {
        await this.load();

        if(callback && typeof callback === 'function') {
            callback();
        }
    }

    async load() {
        try {

            const response = await getNavigatorOHLCData(
                this.properties.marketId,
                this.properties.timelevel!,
                this.properties.endDate,
                this.properties.startDate,
                true,
                this.properties.currency,
                this.properties.showElectedReversals
            );
            const extendedResponse = this.extendData(response.ohlcData);
            const firstDate = response.ohlcData[response.ohlcData.length - 1].x;

            if(this.properties.showElectedReversals && response.electedReversals) {
                for(const electedReversal of response.electedReversals) {
                    if(!this.state.electedReversals[electedReversal.x]) {
                        this.state.electedReversals[electedReversal.x] = [];
                    }
                    this.state.electedReversals[electedReversal.x].push(electedReversal);
                }
            }

            this.state = {
                ...this.state,
                marketName: response.marketName!,
                currencyName: response.currency,
                ohlcData: extendedResponse
            };

            const data = extendedResponse.map((ohlc: any) => ([ohlc.x, ohlc.open, ohlc.high, ohlc.low, ohlc.close]));
            const navData = extendedResponse.map((ohlc: any) => ([ohlc.x, ohlc.close || 0]));

            const { marketName, currencyName, timeLevelName, iteration, isRangeSelectorEnabled, studyCount } = {
                marketName: this.state.marketName,
                currencyName: this.state.currencyName,
                timeLevelName: TimeLevel.toString(this.properties.timelevel!),
                iteration: this.properties.iteration || '',
                isRangeSelectorEnabled: !this.properties.hideNavigator,
                studyCount: this.properties.studyType?.length || 0
            };
            let hideTimelevel = this.properties.hideTimelevel;
            this.instance?.destroy();
            this.instance = Highcharts.stockChart(this.properties.containerId, {
                chart: {
                    height: 500,
                    spacingBottom: studyCount > 0 ? 50 : 15,
                    events: {
                        load: function () {
                            drawCurrencyOnChart(this, currencyName, iteration, isRangeSelectorEnabled);
                            drawTimePeriodOnChart(this, timeLevelName, iteration, isRangeSelectorEnabled, hideTimelevel);
                            if(studyCount > 0) {
                                drawLegendLabelOnChart(this, iteration);
                            }
                        }
                    }
                },
                rangeSelector: {
                    enabled: this.properties.hideNavigator ? false : true,
                    buttons: getRangeSelectorOptions(this.properties.timelevel!)
                },
                title: {
                    text: marketName
                },
                legend: {
                    enabled: true
                },
                tooltip: {
                    xDateFormat: getDateFormatterByTimelevel(this.properties.timelevel!),
                    formatter: function(tooltip: any) {
                        let tooltips: string[] = tooltip.defaultFormatter.call(this, tooltip);

                        for(let i = 0; i < tooltips.length; i++) {
                            if(tooltips[i].indexOf('Price Data') > -1) {
                                let electedReversals = (this as any).point.options?.custom?.electedReversals;
                                if(electedReversals) {
                                    tooltips[i] += `<br/><span style="color:white">●</span> <b> Elected Reversal</b>`;

                                    for(const electedReversal of electedReversals.slice(0,electedReversals.length > 3 ? 2 : 3)) {
                                        tooltips[i] += `<br/> ${electedReversal.y} (${electedReversal.direction === 0 ? 'Bullish' : 'Bearish'})`;
                                    }

                                    if(electedReversals.length > 3) {
                                        tooltips[i] += `<br/> ${electedReversals.length - 2} more reversals...`;
                                    }
                                }
                            }
                        }

                        return tooltips;
                    }
                },
                navigator: {
                    enabled: this.properties.hideNavigator ? false : true,
                    adaptToUpdatedData: false,
                    series: {
                        data: navData,
                        id: 'navigator'
                    },
                    xAxis: {
                        labels: {
                            style: {
                                color: '#FFFFFF'
                            }
                        }
                    }
                },
                xAxis: {
                    id: chartConfig.xAxisName,
                    type: "datetime",
                    ordinal: true,
                    name: "",
                    minRange: 3600000 * 24 * 30, // determine how short the range can be viewed, 5 days, 1 month, etc in milliseconds
                    events: {
                        afterSetExtremes: this.onExtremeUpdate.bind(this)
                    },
                },
                yAxis: [
                    {
                        height: "90%",
                        id: chartConfig.yAxisName,
                        top: '10%',
                        lineWidth: 1,
                        name: "",
                        title: "",
                        opposite: true,
                    }
                ],
                series: [{
                    id: chartConfig.priceDataId,
                    name: 'Price Data',
                    type: 'ohlc',
                    useOhlcData: true,
                    data: data,
                    turboThreshold: 0,
                    showInLegend: false,
                }]
            } as any);

            this.state.xMinExtreme = extendedResponse[0].x;
            this.state.xMaxExtreme = extendedResponse[extendedResponse.length - 1].x;

            if(this.properties.selectedStartDate) {
                this.state.xMinExtreme = convertStringToDate(this.properties.selectedStartDate);
            }

            if(this.properties.selectedEndDate) {
                this.state.xMaxExtreme = convertStringToDate(this.properties.selectedEndDate);
            }

            if(!this.properties.selectedEndDate && !this.properties.selectedStartDate && this.properties.selectedTimeUnits) {
                var fDate = new Date(firstDate);
                var negVal = -1 * this.properties.selectedTimeUnits;
                var date = addTimelevelToDate(new Date(fDate), this.properties.timelevel!, negVal);

                var extendVal = chartConfig.extendTimeUnitInFuture;
                if (this.properties.timelevel === TimeLevelEnum.Daily) {
                    extendVal = 40;
                }

                var enddate = addTimelevelToDate(new Date(fDate), this.properties.timelevel!, extendVal);

                this.state.xMinExtreme = Math.round(date.getTime());

                if (this.properties.timelevel === TimeLevelEnum.Daily || this.properties.timelevel === TimeLevelEnum.Weekly) {
                    this.state.xMaxExtreme = Math.round(enddate.getTime());
                }
            }
            this.instance.xAxis[0].setExtremes(this.state.xMinExtreme, this.state.xMaxExtreme);
            await this.reloadChart(true);

            this.registerArrowKeyAction();
        } catch(err) {
            console.error(err)
        }
    }
}